import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import ScrollToTop from "@/components/site/ScrollToTop";
import SiteLayout from "@/components/site/SiteLayout";
import ThemeInjector from "@/components/ThemeInjector";
import HeadInjector from "@/components/HeadInjector";
import Home from "@/pages/Home";

// Code-splitting: тяжёлые/редкие страницы грузятся по требованию (быстрее первый рендер)
const Catalog = lazy(() => import("@/pages/Catalog"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Services = lazy(() => import("@/pages/Services"));
const NewsList = lazy(() => import("@/pages/NewsList"));
const NewsArticle = lazy(() => import("@/pages/NewsArticle"));
const About = lazy(() => import("@/pages/About"));
const HowTo = lazy(() => import("@/pages/HowTo"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const Login = lazy(() => import("@/pages/admin/Login"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const ProductsAdmin = lazy(() => import("@/pages/admin/ProductsAdmin"));
const NewsAdmin = lazy(() => import("@/pages/admin/NewsAdmin"));
const ServicesAdmin = lazy(() => import("@/pages/admin/ServicesAdmin"));
const CategoriesAdmin = lazy(() => import("@/pages/admin/CategoriesAdmin"));
const SlidesAdmin = lazy(() => import("@/pages/admin/SlidesAdmin"));
const OrdersAdmin = lazy(() => import("@/pages/admin/OrdersAdmin"));
const SettingsAdmin = lazy(() => import("@/pages/admin/SettingsAdmin"));

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-slate-200 border-t-[#E85D2A] rounded-full animate-spin" />
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-[#E85D2A] rounded-full animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <ScrollToTop />
          <ThemeInjector />
          <HeadInjector />
          <Toaster position="top-right" richColors />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/catalog/:categorySlug" element={<Catalog />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/services" element={<Services />} />
              <Route path="/news" element={<NewsList />} />
              <Route path="/news/:slug" element={<NewsArticle />} />
              <Route path="/about" element={<About />} />
              <Route path="/how-to-order" element={<HowTo />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route path="/login" element={<Login />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="products" element={<ProductsAdmin />} />
              <Route path="categories" element={<CategoriesAdmin />} />
              <Route path="news" element={<NewsAdmin />} />
              <Route path="services" element={<ServicesAdmin />} />
              <Route path="slides" element={<SlidesAdmin />} />
              <Route path="orders" element={<OrdersAdmin />} />
              <Route path="settings" element={<SettingsAdmin />} />
            </Route>
          </Routes>
          </Suspense>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
